import { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, Link } from "@/lib/router-compat";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useSEO } from "@/hooks/useSEO";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Loader2, Search, BookOpen, Plus, Check, ArrowLeft, Beaker, Calculator, Globe, Eye, Scale, FlaskConical, Maximize2, Minimize2 } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import ReactMarkdown from "react-markdown";
import remarkMath from "remark-math";
import remarkGfm from "remark-gfm";
import rehypeKatex from "rehype-katex";
import "katex/dist/katex.min.css";
import { MermaidDiagram } from "@/components/ui/MermaidDiagram";
import { ResponsiveTable } from "@/components/ui/ResponsiveTable";
import { getSectionIcon } from "@/lib/sectionIcons";
import { prepareMathContent } from "@/lib/mathUtils";

interface TemplateSheet {
  id: string;
  slug: string;
  category: string;
  difficulty: string;
  title_en: string;
  title_fr: string;
  description_en: string;
  description_fr: string;
  content_en: string;
  content_fr: string;
  tags: string[];
}

const CATEGORY_CONFIG: Record<string, { icon: typeof Beaker; labelKey: string; color: string }> = {
  sciences: { icon: Beaker, labelKey: "templates.sciences", color: "text-emerald-600 bg-emerald-500/10 border-emerald-500/20" },
  maths: { icon: Calculator, labelKey: "templates.maths", color: "text-blue-600 bg-blue-500/10 border-blue-500/20" },
  "culture-generale": { icon: Globe, labelKey: "templates.cultureGenerale", color: "text-amber-600 bg-amber-500/10 border-amber-500/20" },
  medecine: { icon: FlaskConical, labelKey: "templates.medecine", color: "text-primary bg-primary/10 border-primary/20" },
  "prepa-scientifique": { icon: Calculator, labelKey: "templates.prepaScientifique", color: "text-primary bg-primary/10 border-primary/20" },
  "prepa-litteraire": { icon: Globe, labelKey: "templates.prepaLitteraire", color: "text-primary bg-primary/10 border-primary/20" },
  ingenierie: { icon: Beaker, labelKey: "templates.ingenierie", color: "text-primary bg-primary/10 border-primary/20" },
  "ecole-commerce": { icon: Scale, labelKey: "templates.ecoleCommerce", color: "text-primary bg-primary/10 border-primary/20" },
};

export default function TemplateSheetsPage() {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const lang = i18n.language?.startsWith("fr") ? "fr" : "en";

  const [templates, setTemplates] = useState<TemplateSheet[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [activeCategory, setActiveCategory] = useState<string | null>(null);
  const [previewFullscreen, setPreviewFullscreen] = useState(false);
  const [addingIds, setAddingIds] = useState<Set<string>>(new Set());
  const [addedIds, setAddedIds] = useState<Set<string>>(new Set());
  const [previewTemplate, setPreviewTemplate] = useState<TemplateSheet | null>(null);

  useEffect(() => {
    if (!previewFullscreen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        setPreviewFullscreen(false);
      }
    };
    document.addEventListener("keydown", handler, true);
    return () => document.removeEventListener("keydown", handler, true);
  }, [previewFullscreen]);

  useSEO({
    title: t("templates.pageTitle", "Free Revision Sheet Templates – Study Resources | System Atlas"),
    description: t("templates.pageDesc", "Browse free revision sheet templates for medicine, law, engineering, sciences & more. Pre-made structured summaries ready to study from."),
  });

  useEffect(() => {
    const fetchTemplates = async () => {
      const { data, error } = await supabase
        .from("template_sheets" as any)
        .select("*")
        .eq("is_published", true)
        .order("category")
        .order("title_en");

      if (error) {
        console.error("Error fetching templates:", error);
      } else {
        setTemplates((data as any) || []);
      }
      setLoading(false);
    };
    fetchTemplates();
  }, []);

  const filtered = useMemo(() => {
    let result = templates;
    if (activeCategory) result = result.filter(t => t.category === activeCategory);
    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter(t => {
        const title = lang === "fr" ? t.title_fr : t.title_en;
        const desc = lang === "fr" ? t.description_fr : t.description_en;
        return title.toLowerCase().includes(q) || desc.toLowerCase().includes(q) || t.tags.some(tag => tag.toLowerCase().includes(q));
      });
    }
    return result;
  }, [templates, activeCategory, search, lang]);

  const categories = useMemo(() => {
    const cats = [...new Set(templates.map(t => t.category))];
    return cats;
  }, [templates]);

  const handleAddToMySheets = async (template: TemplateSheet, e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    if (!user) {
      navigate("/sign-in");
      return;
    }

    setAddingIds(prev => new Set(prev).add(template.id));

    const content = lang === "fr" ? template.content_fr : template.content_en;
    const title = lang === "fr" ? template.title_fr : template.title_en;

    const { error } = await supabase.from("revision_sheets").insert({
      user_id: user.id,
      content,
      title,
      source_type: "template",
      understanding_status: "new",
    });

    if (error) {
      console.error("Error adding template:", error);
      toast.error(t("templates.addError", "Failed to add sheet"));
    } else {
      setAddedIds(prev => new Set(prev).add(template.id));
      toast.success(t("templates.addSuccess", "Sheet added to My Sheets!"));
    }

    setAddingIds(prev => {
      const next = new Set(prev);
      next.delete(template.id);
      return next;
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  const previewContent = previewTemplate
    ? (lang === "fr" ? previewTemplate.content_fr : previewTemplate.content_en)
    : "";
  const previewTitle = previewTemplate
    ? (lang === "fr" ? previewTemplate.title_fr : previewTemplate.title_en)
    : "";

  return (
    <div className="max-w-5xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="mb-8">
        <Link to="/learn" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-4">
          <ArrowLeft className="w-4 h-4" />
          {t("templates.backToLearn", "Back to Learn")}
        </Link>
        <div className="flex items-center gap-3 mb-2">
          <BookOpen className="w-7 h-7 text-primary" />
          <h1 className="text-3xl font-bold" style={{ fontFamily: 'var(--font-serif)' }}>
            {t("templates.title", "Revision Sheet Library")}
          </h1>
        </div>
        <p className="text-muted-foreground max-w-2xl">
          {t("templates.subtitle", "Pre-made revision sheets on popular topics. Add them to your collection, quiz yourself, and track your progress. just like your own sheets.")}
        </p>
      </div>

      {/* Search & Filters */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 mb-6">
        <div className="relative flex-1 w-full">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder={t("templates.searchPlaceholder", "Search sheets...")}
            className="pl-9"
          />
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button
            variant={activeCategory === null ? "default" : "outline"}
            size="sm"
            onClick={() => setActiveCategory(null)}
          >
            {t("templates.all", "All")}
          </Button>
          {categories.map(cat => {
            const config = CATEGORY_CONFIG[cat];
            const Icon = config?.icon || BookOpen;
            return (
              <Button
                key={cat}
                variant={activeCategory === cat ? "default" : "outline"}
                size="sm"
                className="gap-1.5"
                onClick={() => setActiveCategory(cat)}
              >
                <Icon className="w-3.5 h-3.5" />
                {t(config?.labelKey || `templates.${cat}`, cat)}
              </Button>
            );
          })}
        </div>
      </div>

      {/* Results */}
      {filtered.length === 0 ? (
        <div className="text-center py-16">
          <BookOpen className="w-12 h-12 text-muted-foreground/30 mx-auto mb-3" />
          <p className="text-muted-foreground">
            {templates.length === 0
              ? t("templates.emptyLibrary", "The library is being prepared. Check back soon!")
              : t("templates.noResults", "No sheets match your search.")}
          </p>
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {filtered.map(template => {
            const title = lang === "fr" ? template.title_fr : template.title_en;
            const description = lang === "fr" ? template.description_fr : template.description_en;
            const config = CATEGORY_CONFIG[template.category];
            const isAdding = addingIds.has(template.id);
            const isAdded = addedIds.has(template.id);

            return (
              <Card
                key={template.id}
                className="group hover:shadow-md transition-shadow border-border"
              >
                <Link to={`/learn/sheets/${template.slug}`} className="block">
                <CardContent className="p-5 flex flex-col h-full">
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <Badge variant="outline" className={cn("text-xs capitalize", config?.color)}>
                      {t(config?.labelKey || `templates.${template.category}`, template.category)}
                    </Badge>
                    <Badge variant="outline" className="text-xs text-muted-foreground capitalize">
                      {template.difficulty}
                    </Badge>
                  </div>
                  <h3 className="font-semibold text-sm mb-1.5 line-clamp-2">{title}</h3>
                  <p className="text-xs text-muted-foreground mb-4 line-clamp-3 flex-1">{description}</p>
                  {template.tags.length > 0 && (
                    <div className="flex flex-wrap gap-1 mb-3">
                      {template.tags.slice(0, 3).map(tag => (
                        <span key={tag} className="text-[10px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground">{tag}</span>
                      ))}
                    </div>
                  )}
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      className="flex-1 gap-1.5"
                      onClick={(e) => { e.stopPropagation(); setPreviewTemplate(template); }}
                    >
                      <Eye className="w-3.5 h-3.5" />
                      {t("templates.preview", "Preview")}
                    </Button>
                    <Button
                      size="sm"
                      variant={isAdded ? "outline" : "default"}
                      className="flex-1 gap-1.5"
                      onClick={(e) => handleAddToMySheets(template, e)}
                      disabled={isAdding || isAdded}
                    >
                      {isAdding ? (
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      ) : isAdded ? (
                        <Check className="w-3.5 h-3.5" />
                      ) : (
                        <Plus className="w-3.5 h-3.5" />
                      )}
                      {isAdded
                        ? t("templates.added", "Added!")
                        : t("templates.add", "Add")}
                    </Button>
                  </div>
                </CardContent>
                </Link>
              </Card>
            );
          })}
        </div>
      )}

      {/* Preview Dialog */}
      <Dialog open={!!previewTemplate} onOpenChange={(open) => { if (!open) { setPreviewTemplate(null); setPreviewFullscreen(false); } }}>
        <DialogContent className={cn(
          "flex flex-col overflow-hidden transition-all duration-200",
          previewFullscreen
            ? "max-w-[95vw] max-h-[95vh] w-[95vw] h-[95vh]"
            : "max-w-3xl w-[95vw] h-[85vh]"
        )}>
          <DialogHeader className="flex flex-row items-center justify-between gap-2">
            <DialogTitle className="text-xl flex-1" style={{ fontFamily: 'var(--font-serif)' }}>
              {previewTitle}
            </DialogTitle>
            <Button
              variant="ghost"
              size="icon"
              className="shrink-0 mr-6"
              onClick={() => setPreviewFullscreen(f => !f)}
              title={previewFullscreen ? t("common.exitFullscreen", "Exit fullscreen") : t("common.fullscreen", "Fullscreen")}
            >
              {previewFullscreen ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
            </Button>
          </DialogHeader>
          <div className="flex-1 min-h-0 overflow-hidden">
            <ScrollArea className="h-full">
              <div className="revision-prose max-w-none pb-4 px-1">
              <ReactMarkdown
                remarkPlugins={[remarkMath, remarkGfm]}
                rehypePlugins={[rehypeKatex]}
                components={{
                  table: ({ children }) => <ResponsiveTable>{children}</ResponsiveTable>,
                  h2({ children }) {
                    const text = String(children);
                    const Icon = getSectionIcon(text);
                    return (
                      <h2 className="flex items-center gap-2.5">
                        <span className="inline-flex items-center justify-center w-7 h-7 rounded-lg bg-primary/10 shrink-0">
                          <Icon className="w-4 h-4 text-primary" />
                        </span>
                        {children}
                      </h2>
                    );
                  },
                  code({ className, children, ...props }) {
                    const content = String(children).trim();
                    if (/language-mermaid/.test(className || "") || /^(graph|flowchart|sequenceDiagram|classDiagram|stateDiagram|erDiagram|gantt|pie|mindmap|gitgraph)\b/.test(content)) {
                      return <MermaidDiagram chart={content} />;
                    }
                    return <code className={className} {...props}>{children}</code>;
                  },
                }}
              >
                {prepareMathContent(previewContent)}
              </ReactMarkdown>
              </div>
            </ScrollArea>
          </div>
          {previewTemplate && (
            <div className="pt-3 border-t border-border flex justify-end gap-2">
              <Button variant="outline" onClick={() => setPreviewTemplate(null)}>
                {t("common.close", "Close")}
              </Button>
              <Button
                className="gap-2"
                onClick={() => { handleAddToMySheets(previewTemplate); setPreviewTemplate(null); }}
                disabled={addedIds.has(previewTemplate.id)}
              >
                {addedIds.has(previewTemplate.id) ? <Check className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
                {addedIds.has(previewTemplate.id)
                  ? t("templates.added", "Added!")
                  : t("templates.addToSheets", "Add to My Sheets")}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
